import {EMPTY, forkJoin as observableForkJoin, Observable, of, of as observableOf} from 'rxjs';

import {catchError, map, switchMap, takeUntil} from 'rxjs/operators';
import {Component, Input, ViewChild} from '@angular/core';
import {ExecutorService} from '../../../../../core/executor/executor.service';
import {GenericElementValidationExecutionStepsFactory} from '../../../services/generic/generic-element-validation-execution-steps-factory';
import {Element} from '../../../../services/element/element';
import {FieldMetadataGrid} from '../../../../services/module/module-element-field-metadata-grid';
import {ModuleElement} from '../../../../services/module/module-element';
import {DynamicTableComponent} from '../../../../dynamic-table/dynamic-table.component';
import {TableColumn} from '../../../../dynamic-table/shared/table-column';
import {EntityValidatorStatus} from '../../../../validators/services/entity-validator';
import {GenericTurboGridComponent} from '../../generic-turbo-grid/generic-turbo-grid.component';
import {CancelComponentChangesService} from '../../../services/cancel-components-changes.service';
import {GenericGridRemoteFilterService} from '../../generic-grid/services/generic-grid-remote-filter.service';
import {GenericGridGlobalFilterService} from '../../generic-grid/services/generic-grid-global-filter.service';
import {GenericGridColumnBuilderService} from '../../generic-grid/services/generic-grid-column-builder.service';
import {GenericGridBulkSaveService} from '../../generic-grid/services/generic-grid-bulk-save.service';
import {GenericGridEntityService} from '../../../services/generic/entity/generic-grid-entity.service';
import {GenericElementInlineEditorService} from '../../../services/generic/generic-element-inline-editor.service';
import {GenericElementFilterService} from '../../../services/generic/filter/generic-element-filter.service';
import {GenericTurboGridLayoutService} from '../../generic-turbo-grid/service/generic-turbo-grid-layout-service';
import {GenericGridSingleEntitySaveService} from '../../generic-grid/services/generic-grid-single-entity-save.service';
import {GenericGridLayoutService} from '../../generic-grid/services/generic-grid-layout.service';
import {EntityStatus} from '../../../../services/entity/entity-status';
import {TableRow} from '../../../../dynamic-table/shared/table-row';
import {cloneDeep} from 'lodash';
import {Guid} from 'guid-typescript';
import {ExecutorActionEvent} from '../../../../../core/executor/service/executor-actions/executor-action-event';
import {JobContext} from '../../../../../core/job-runner/context/job.context';
import {RunnableEventRegistry} from '../../../../../core/job-runner/type/runnable-event.registry';
import {Entity} from '../../../../helpers/entity';
import {ChangeDetectorRefHelper} from '../../../../helpers/change-detector-ref.helper';
import {CustomerInvoicePositionDetailTableComponent} from './customer-invoice-position-detail-table.component';
import {CustomerInvoicePositionDetailInvoiceContextTableComponent} from './customer-invoice-position-detail-invoice-context-table.component';
import {LeasedEmployeeAutocompleteHandlerService} from './autocomplete-handler/leased-employee-autocomplete-handler.service';

@Component({
  selector: 'app-custom-customer-invoice-position-table',
  templateUrl: './customer-invoice-position-table.component.html',
  providers: [
    CancelComponentChangesService,
    GenericGridRemoteFilterService,
    GenericGridGlobalFilterService,
    GenericGridColumnBuilderService,
    GenericGridBulkSaveService,
    GenericGridEntityService,
    GenericElementInlineEditorService,
    GenericElementFilterService,
    GenericElementValidationExecutionStepsFactory,
    GenericTurboGridLayoutService,
    ExecutorService,
    GenericGridSingleEntitySaveService,
    GenericGridLayoutService
  ],
  styles: [`
    ::ng-deep .grid-container {
      height: 100%;
    }
  `]
})
export class CustomerInvoicePositionTableComponent extends GenericTurboGridComponent {
  @Input() element: Element;
  @Input() fields: Array<FieldMetadataGrid>;
  @Input() toolbarItems: any[] = [];
  @Input() statusBarItems: any[] = [];
  @Input() moduleElement: ModuleElement;
  @Input() masterEntity: any = null;
  @Input() masterField: any = null;
  @Input() isPart = false;

  public expanderModuleElement: ModuleElement;
  public isEditable = true;
  public leasedEmployeeDialogVisible = false;
  public lastSelectedOrUnselectedEntity: any = null;
  public invoice: { isSimpleView: boolean } = null;

  @ViewChild('table', {static: false}) table: DynamicTableComponent;
  @ViewChild(CustomerInvoicePositionDetailInvoiceContextTableComponent, { static: false }) invoicePositionsDetailsTable: CustomerInvoicePositionDetailInvoiceContextTableComponent;

  public expandedRowKeys: { [s: string]: boolean; } = {};
  public columns: TableColumn[] = [];
  public rows: TableRow[] = [];
  public entities: any[] = [];
  public totalCount = 0;
  public expanderHasChanges = false;
  public enableKeyboardAdd = false;

  public toolbarContextName = 'customerInvoicePositionComponent';

  public ngOnInit() {
    super.ngOnInit();

    this.onComponentInit();
  }

  public onComponentInit(): void {
    this.elementContext = this.createContext();
    this.initColumns();
    this.layoutService.layoutSizeChanged$.subscribe(() => {
      setTimeout(() => {
        this.setTableScrollHeightAndWidth();
      }, 50);
    });

    if (this.element.datamodel && this.hasMasterElement()) {
      const context = new JobContext();
      context.identifier = Guid.create().toString();
      context.component = this;
      context.event = RunnableEventRegistry.PostInit;

      this.jobContainerService.runRelevantJobs(context);
    }
    this.embeddedFields.push('leasedEmployee');

    if (!this.invoice) {
      const invoiceId = this.elementContext.getMasterEntity('invoice').value;

      if (!isNaN(+invoiceId)) {
        this.genericCrudService.getEntity('phoenix/invoices', invoiceId, '', { embedded: 'none' }).subscribe((invoice) => {
          this.invoice = invoice;
          ChangeDetectorRefHelper.detectChanges(this);
        });
      } {
        this.invoice = {
          isSimpleView: false
        };
        ChangeDetectorRefHelper.detectChanges(this);
      }
    }
  }

  public onLeasedEmployeeOptionChanged(leasedEmployee) {
    this.entityManager.persistMore(this.lastSelectedOrUnselectedEntity, [
      {property: EntityStatus.ENTITY_CHANGED_FLAG, newValue: true, force: true},
      {property: 'leasedEmployee', newValue: leasedEmployee, force: true}
    ]);

    this.lastSelectedOrUnselectedEntity._embedded = this.lastSelectedOrUnselectedEntity._embedded || {};
    this.lastSelectedOrUnselectedEntity._embedded.leasedEmployee = leasedEmployee;

    this.entities = [...this.entities];

    this.leasedEmployeeDialogVisible = false;

    this.onValueChanged(this.lastSelectedOrUnselectedEntity, true);
  }

  public onLeasedEmployeeDialogOpen(entity): void {
    this.lastSelectedOrUnselectedEntity = entity;
    this.leasedEmployeeDialogVisible = true;
  }

  public initColumns(): this {
    const editable = this.isEditable;
    const leasedEmployeeHandler = new LeasedEmployeeAutocompleteHandlerService(this.genericCrudService);

    this.columns = [
      {
        key: 'leasedEmployee',
        header: this.translationService.instant('INVOICE.LEASED_EMPLOYEE'),
        renderer: (entity) => {
          const firstName = Entity.getValue(entity, 'leasedEmployee.firstName') || Entity.getValueInEmbedded(entity, 'leasedEmployee.firstName') || '',
            lastName = Entity.getValue(entity, 'leasedEmployee.lastName') || Entity.getValueInEmbedded(entity, 'leasedEmployee.lastName') || '';
          return `${firstName} ${lastName}`;
        },
        edit: editable ? {
          type: 'autocomplete',
          autocompleteHandler: leasedEmployeeHandler
        } : undefined,
        style: {
          width: '80px'
        },
        sort: {
          key: 'leasedEmployee'
        }
      },
      {
        key: 'description',
        header: this.translationService.instant('INVOICE.DESCRIPTION'),
        edit: editable ? {} : undefined,
        sort: {
          key: 'description'
        }
      },
      {
        key: 'description2',
        header: this.translationService.instant('INVOICE.DESCRIPTION2'),
        edit: editable ? {} : undefined,
        sort: {
          key: 'description2'
        }
      },
      {
        key: 'sortPosition',
        header: this.translationService.instant('INVOICE.SORT_POSITION'),
        style: {
          width: '30px'
        },
        sort: {
          key: 'sortPosition'
        }
      },
      {
        key: 'menu',
        header: '',
        style: {
          width: '60px',
          textAlign: 'center'
        },
        menu: editable ? {
          buttons: [
            {click: this.onAddInvoicePositionDetail.bind(this), icon: 'fa fa-plus', condition: (entity) => typeof entity.isEditable === 'undefined' || entity.isEditable === true },
            {click: this.onSortPositionUp.bind(this), icon: 'fa fa-arrow-up', condition: (entity) => typeof entity.isEditable === 'undefined' || entity.isEditable === true},
            {click: this.onSortPositionDown.bind(this), icon: 'fa fa-arrow-down', condition: (entity) => typeof entity.isEditable === 'undefined' || entity.isEditable === true},
            {click: this.onDelete.bind(this), icon: 'fa fa-trash', condition: (entity) => typeof entity.isEditable === 'undefined' || entity.isEditable === true}
          ]
        } : undefined
      }
    ];

    return this;
  }

  public onCellEdit(event: any): void {
    const column = event.column,
      entity = event.entity,
      originalEvent = event.originalEvent;
    let entityValue = originalEvent.target ? originalEvent.target.value : originalEvent.value;
    if (column.edit && column.edit.type === 'autocomplete') {
      entityValue = originalEvent;
      if (column.key === 'leasedEmployee') {
        this.onLeasedEmployeeOptionChanged(entityValue);
      }
    }

    this.entityManager.persist(entity, {property: EntityStatus.ENTITY_CHANGED_FLAG, newValue: true, force: true});
    this.entityManager.persist(entity, {property: column.key, newValue: entityValue, force: true });

    this.onValueChanged(entity, entityValue);
  }

  public getSelectedEntity(): any {
    return this.selectedMasterEntity || null;
  }

  public recheckToolbarItems(): void {
    this.toolbarItemCheckService.check(this);
  }

  public hasChanges(checkEmbedded: boolean = false): boolean {
    return super.hasChanges(checkEmbedded) || this.expanderHasChanges;
  }

  public onRefresh(): Observable<any> {
    this.expanderHasChanges = false;

    return super.onRefresh().pipe(map(() => {
      this.updateRowsStyle();
      this.expandAll();
    }));
  }

  public onSave(): Observable<any> {
    const observables = [];

    const createdEntities = this.getCreatedEntities(false);
    for (const entity of createdEntities) {
      this.entityManager.persist(entity, { property: 'invoice', newValue: this.invoice, force: true })
      if (!entity[EntityStatus.ENTITY_DRAFT_DELETED_FLAG]) {
        observables.push(
          this.genericCrudService.createEntity(`phoenix/invoicepositions`, entity, false, {
            embedded: 'leasedEmployee'
          }).pipe(
            map((createdEntity) => {
              const index = this.findEntityIndex(entity);

              if (index !== -1) {
                this.entities[index] = createdEntity;
              }

              return createdEntity;
            })
          )
        );
      }
    }

    const updatedEntities = this.getUpdatedEntities(false);
    for (const entity of updatedEntities) {
      this.entityManager.persist(entity, { property: 'invoice', newValue: this.invoice, force: true })
      if (!entity[EntityStatus.ENTITY_DRAFT_DELETED_FLAG]) {
        observables.push(
          this.genericCrudService.editEntity(`phoenix/invoicepositions/${entity.id}`, entity, {
            embedded: 'leasedEmployee'
          }).pipe(
            map((updatedEntity) => {
              const index = this.findEntityIndex(entity);

              if (index !== -1) {
                this.entities[index] = updatedEntity;
              }

              return updatedEntity;
            })
          )
        );
      }
    }

    const deletedEntitiesDraft = this.getDraftDeletedEntities();
    for (const entity of deletedEntitiesDraft) {
      this.entityManager.persist(entity, { property: 'invoice', newValue: this.invoice, force: true })
      observables.push(
        this.genericCrudService.deleteEntity(`phoenix/invoicepositions/${entity.id}`)
      );
    }

    if (observables.length === 0) {
      return of({
        status: true,
        content: [],
        message: GenericGridBulkSaveService.SAVE_MESSAGE_NOTHING_TO_SAVE
      })
    }

    return observableForkJoin(observables).pipe(
      catchError((response: any) => {
        return observableOf(response);
      }))
      .pipe(
        map((results) => {
          return {
            status: true,
            content: results
          };
        }),
        takeUntil(this.unsubscribe),
      );
  }

  public onAfterSave(): Observable<any> {
    return observableOf(null);
  }

  public onChange(): Observable<any> {
    return observableOf(null);
  }

  public doValidate(): Observable<EntityValidatorStatus> {
    return observableOf({
      entity: null,
      isValid: true,
      error: '',
      errorFields: []
    });
  }

  public onRowExpanded(): void {
    ChangeDetectorRefHelper.detectChanges(this);

    setTimeout(() => {
      this.genericGridLayoutService
        .setGrid(this)
        .adaptRowsColor();
    }, 5);
  }

  public onRowSelected(event): void {
    this.lastSelectedOrUnselectedEntity  = event.data;

    setTimeout(() => {
      this.genericGridLayoutService
        .setGrid(this)
        .adaptRowsColor();
    }, 5);
  }

  public onRowUnselected(event): void {
    this.lastSelectedOrUnselectedEntity  = event.data;

    setTimeout(() => {
      this.genericGridLayoutService
        .setGrid(this)
        .adaptRowsColor();
    }, 5);
  }

  public onExpanderComponentMetaChange(): void {
    this.expanderHasChanges = true;

    this.toolbarItemCheckService.check(this);
  }

  public onLazyLoad(event): void {
    this.currentOffset = event.first;
    this.defaultPageSize = event.rows;
    this.sortField = event.sortField || 'id';
    this.sortDirection = event.sortOrder === '1' ? 'asc' : 'desc';

    if (this.element.autoloadData) {
      this.loadEntities().subscribe();
    }
  }

  public startNewEntityAdd(entity?: any): void {
    if (this.invoice.isSimpleView) {
      this.addNewInvoiceDetail(entity);
    } else {
      this.addNewInvoicePosition(entity);
    }

    this.updateRowsStyle();

    setTimeout(() => {
      this.genericGridLayoutService
        .setGrid(this)
        .adaptRowsColor();
    }, 5);
  }

  protected addNewInvoicePosition(entity) {
    entity = entity || this.emptyEntity;
    entity.sortPosition = 0;
    entity.fqn = 'PhoenixBundle\\Entity\\InvoicePosition';
    const newEntity = cloneDeep(entity);

    newEntity[EntityStatus.ENTITY_DRAFT_FLAG] = Guid.create().toString();
    newEntity.isEditable = true;

    this.entityFactory.assignMasterEntities(newEntity, this.getElementContext().getMasterEntities());
    this.entities = [...this.entities, newEntity];
    this.expandAll();
    ChangeDetectorRefHelper.detectChanges(this);

    this.notifySlaves(ExecutorActionEvent.EntitiesChanged)
      .notifyMaster(ExecutorActionEvent.EntitiesChanged);

    this.executeAction(ExecutorActionEvent.AfterNewEntityAdded, {
      component: this,
      entity: newEntity
    }).subscribe();
    this.executeAction(ExecutorActionEvent.EntitiesChanged, this).subscribe();
  }

  protected addNewInvoiceDetail(entity: any): void {
    this.invoicePositionsDetailsTable.startNewEntityAdd(entity);
  }

  protected onEntitiesChanged(): void {
    for (const entity of this.entities) {
      entity[EntityStatus.ENTITY_CHANGED_FLAG] = false;
      entity.sortPosition = entity.sortPosition || 0;
      entity.isEditable = this.isEditable;
    }

    this.updateRowsStyle();
    this.setTableScrollHeightAndWidth();
    this.expandAll();

    ChangeDetectorRefHelper.detectChanges(this);
  }

  protected expandAll(): void {
    const expandedRowsKeys: { [s: string]: boolean; } = {};

    for (const entity of this.entities) {
      if (entity[EntityStatus.ENTITY_DRAFT_FLAG]) {
        expandedRowsKeys[entity[EntityStatus.ENTITY_DRAFT_FLAG]] = true;
      }
    }

    this.expandedRowKeys = {...expandedRowsKeys};
  }

  protected setTableScrollHeightAndWidth(): void {
    const containerHeight = this._gridContainer.nativeElement.clientHeight;

    if (this.table && containerHeight) {
      this.table.height = containerHeight - 7;
      ChangeDetectorRefHelper.detectChanges(this);
    }
  }

  protected onSortPositionUp(entity): void {
    this.table.selectedEntity = entity;

    if (entity.sortPosition !== 0) {
      const sortPosition = entity.sortPosition -= 1;

      this.entityManager.persist(entity, {property: 'sortPosition', newValue: sortPosition, force: true});
      this.entityManager.persist(entity, {property: EntityStatus.ENTITY_CHANGED_FLAG, newValue: true, force: true});

      this.entities = this.entities.sort(function(a, b) {
        return a.sortPosition - b.sortPosition;
      });

      this.updateRowsStyle();
      this.onValueChanged(entity, sortPosition);
    }
  }

  protected onSortPositionDown(entity): void {
    this.table.selectedEntity = entity;

    const sortPosition = entity.sortPosition += 1;

    this.entityManager.persist(entity, {property: 'sortPosition', newValue: sortPosition, force: true});
    this.entityManager.persist(entity, {property: EntityStatus.ENTITY_CHANGED_FLAG, newValue: true, force: true});

    this.entities = this.entities.sort(function(a, b) {
      return a.sortPosition - b.sortPosition;
    });

    this.updateRowsStyle();
    this.onValueChanged(entity, sortPosition);
  }

  protected onDelete(entity): void {
    if (entity && entity.id) {
      this.removeEntity(entity);
      this.genericCrudService.deleteEntity(`phoenix/invoicepositions/${entity.id}`).subscribe();
    }
    this.removeEntity(entity);
  }

  protected onAddInvoicePositionDetail(entity): void {
    const invoicePositionsDetailsComponent = this.table.expanderComponents.find((aComponent: CustomerInvoicePositionDetailTableComponent) =>
      aComponent.invoicePosition[EntityStatus.ENTITY_DRAFT_FLAG] === entity[EntityStatus.ENTITY_DRAFT_FLAG]);

    if (invoicePositionsDetailsComponent) {
      invoicePositionsDetailsComponent.startNewEntityAdd();
    }
  }

  public onValueChanged(entity: any, value: any): void {
    this.getEntityDataStore()
      .onEntityValueChanged(this.getElementDataModelApiRoute(), {
        entity: entity,
        gridField: null,
        value: value
      });

    setTimeout(() => {
      this.genericGridLayoutService
        .setGrid(this)
        .adaptRowsColor();
    }, 5);
  }

  private updateRowsStyle(): void {
    this.rows = [];

    for (const entity of this.entities) {
      this.rows.push({
        entity: entity,
        name: 'description',
        getClass: () => {
          entity['cssColor'] = 'color-lightskyblue';
          return this.getRowStyleClass(entity);
        },
        showExpander: entity
      });
    }
  }
}
